import React, { useEffect } from 'react';
import { useSearchStore, SearchScope } from '../stores/useSearchStore';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';
import { useDocumentStore } from '../stores/useDocumentStore';
import { useUIStore } from '../stores/useUIStore';
import { Search, FileText, CheckSquare, X } from 'lucide-react';

export const CommandPaletteModal: React.FC = () => {
  const { isOpen, setOpen, query, setQuery, activeScope, setActiveScope } = useSearchStore();
  const { currentPackage } = useWorkspaceStore();
  const { setActiveDocument } = useDocumentStore();
  const { setActiveTab } = useUIStore();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(!isOpen);
      }
      if (e.key === 'Escape' && isOpen) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, setOpen]);

  if (!isOpen) return null;

  const allDocs = currentPackage?.documents || [];
  const allTasks = currentPackage?.taskMatrix || [];

  // Scoped Search Matching Logic
  const matchingDocs = allDocs.filter(d => {
    if (activeScope !== 'all' && activeScope !== 'doc') return false;
    if (!query.trim()) return true;
    return d.name.toLowerCase().includes(query.toLowerCase()) || d.content.toLowerCase().includes(query.toLowerCase());
  });

  const matchingTasks = allTasks.filter(t => {
    if (activeScope !== 'all' && activeScope !== 'task') return false;
    if (!query.trim()) return true;
    return t.description.toLowerCase().includes(query.toLowerCase()) || (t.category && t.category.toLowerCase().includes(query.toLowerCase()));
  });

  const handleSelectDoc = (docPath: string) => {
    setActiveDocument(docPath);
    setActiveTab('reader');
    setOpen(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-start justify-center pt-24 p-4">
      <div className="glass-panel-heavy w-full max-w-2xl rounded-xl border border-white/20 shadow-2xl overflow-hidden flex flex-col">
        {/* Search Input Bar */}
        <div className="p-4 border-b border-white/10 flex items-center space-x-3 bg-slate-900/60">
          <Search className="w-5 h-5 text-blue-400 shrink-0" />
          <input
            type="text"
            autoFocus
            placeholder="Search documents, tasks, code snippets, or tags (Cmd+K)..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full bg-transparent text-sm text-white focus:outline-none font-mono placeholder:text-slate-500"
          />
          <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-200">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scope Filter Buttons */}
        <div className="px-4 py-2 bg-slate-900/40 border-b border-white/5 flex items-center space-x-2 text-xs font-mono">
          {(['all', 'doc', 'task', 'code', 'tag'] as SearchScope[]).map((scope) => (
            <button
              key={scope}
              onClick={() => setActiveScope(scope)}
              className={`px-2.5 py-1 rounded-md uppercase font-semibold transition-colors ${
                activeScope === scope
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:text-slate-200'
              }`}
            >
              {scope === 'doc' ? 'Docs' : scope === 'task' ? 'Tasks' : scope}
            </button>
          ))}
        </div>

        {/* Search Results List */}
        <div className="max-h-96 overflow-y-auto divide-y divide-white/5 p-2">
          {matchingDocs.length === 0 && matchingTasks.length === 0 && (
            <div className="p-8 text-center text-xs text-slate-500 font-mono">
              No matching specifications or tasks found.
            </div>
          )}

          {matchingDocs.map((d) => (
            <button
              key={d.path}
              onClick={() => handleSelectDoc(d.path)}
              className="w-full text-left p-3 hover:bg-slate-800/50 rounded-lg transition-colors flex items-center space-x-3 text-xs"
            >
              <FileText className="w-4 h-4 text-blue-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-200 truncate">{d.name}</div>
                <div className="text-[11px] text-slate-400 truncate mt-0.5">{d.meta.title}</div>
              </div>
              <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
                Doc
              </span>
            </button>
          ))}

          {matchingTasks.map((t) => (
            <button
              key={t.id}
              onClick={() => handleSelectDoc(t.documentPath)}
              className="w-full text-left p-3 hover:bg-slate-800/50 rounded-lg transition-colors flex items-center space-x-3 text-xs"
            >
              <CheckSquare className="w-4 h-4 text-emerald-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-200 truncate">{t.description}</div>
                <div className="text-[11px] text-slate-400 truncate mt-0.5 font-mono">{t.documentPath} • Line {t.lineNumber}</div>
              </div>
              <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                Task ({t.status})
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
